import { PlaintiffDefault } from '@ee/common/enums';

/**
 * EE-367: the plaintiff options for a client account, and what the case plaintiff field holds.
 *
 * The options and the case value are different things. Picking or typing a name writes the case
 * (`company_name` / the intake `plaintiff` key). Whether a typed name is also added to the account
 * list is the service's call (`createEviction`), and it never adds one for a restricted client.
 */

/**
 * A client account's plaintiff options and how the case plaintiff field behaves, as
 * `GET /api/clients/{clientId}/attorney/{attorneyId}/plaintiff-options` returns them.
 *
 * `options` is computed by the service: the company name first unless hidden, then the saved names.
 * `restricted` means the field accepts only a value from `options`.
 */
export interface PlaintiffOptions {
  options: string[];
  company_name: string | null;
  restricted: boolean;
  default: PlaintiffDefault;
  hide_company_name: boolean;
}

/** Trim, drop blanks, and keep the first spelling of a case-insensitive duplicate. */
export function normalizePlaintiffNames(
  names: readonly (string | null | undefined)[] | null | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names ?? []) {
    const name = typeof raw === 'string' ? raw.trim() : '';
    if (!name) {
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(name);
  }
  return out;
}

/**
 * Names to offer while the field holds `query`.
 *
 * A blank query shows the whole list. A query that equals a name is a selection, not a search, so
 * reopening the dropdown still shows every name, with that name first: the restricted field
 * highlights the first option, and Enter must keep the current name rather than swap in another.
 * Anything else narrows to case-insensitive matches.
 */
export function filterPlaintiffNames(names: readonly string[], query: string | null | undefined): string[] {
  const saved = normalizePlaintiffNames(names);
  const q = (query ?? '').trim().toLowerCase();
  if (!q) {
    return saved;
  }
  const exact = saved.find(name => name.toLowerCase() === q);
  if (exact) {
    return [exact, ...saved.filter(name => name !== exact)];
  }
  return saved.filter(name => name.toLowerCase().includes(q));
}

/** The option matching `name` ignoring case and surrounding space, in the option's own spelling. */
export function findPlaintiffOption(options: readonly string[], name: string | null | undefined): string | null {
  const key = (name ?? '').trim().toLowerCase();
  if (!key) {
    return null;
  }
  return options.find(option => option.trim().toLowerCase() === key) ?? null;
}

/**
 * Plain suggestions with no client-account rules: PM property owners, or public intake's typed
 * company name. Free text, nothing prefilled by these options (callers patch their own value).
 */
export function suggestedPlaintiffOptions(names: readonly (string | null | undefined)[]): PlaintiffOptions {
  return {
    options: normalizePlaintiffNames(names),
    company_name: null,
    restricted: false,
    default: PlaintiffDefault.BLANK,
    hide_company_name: false
  };
}

/** Today's field, used when the options cannot be loaded: free text, company name offered and prefilled. */
export function fallbackPlaintiffOptions(companyName: string | null | undefined): PlaintiffOptions {
  const company = companyName?.trim() || null;
  return {
    options: company ? [company] : [],
    company_name: company,
    restricted: false,
    default: PlaintiffDefault.COMPANY_NAME,
    hide_company_name: false
  };
}

/**
 * A cloned case's plaintiff, when the new case is for the same client. Cloning and then switching to
 * another client must not carry the source client's plaintiff over (it would also be auto-added to
 * the new client's list on save).
 */
export function cloneNameFor(
  cloneCase: { company_name?: string | null; client_id?: string | null } | null | undefined,
  clientId: string | null | undefined
): string | null {
  if (!cloneCase?.company_name) {
    return null;
  }
  return !cloneCase.client_id || cloneCase.client_id === clientId ? cloneCase.company_name : null;
}

/**
 * What a new case's plaintiff starts as.
 *
 * A clone keeps its source's plaintiff unless the client is restricted and that name is not an
 * option. A restricted client with a single option gets it preselected. Otherwise the company name
 * when it is the default and not hidden, else blank.
 */
export function plaintiffPrefill(opts: PlaintiffOptions, cloneName?: string | null): string {
  const clone = cloneName?.trim() ?? '';
  if (clone) {
    if (!opts.restricted) {
      return clone;
    }
    const match = findPlaintiffOption(opts.options, clone);
    if (match) {
      return match;
    }
  }
  if (opts.restricted && opts.options.length === 1) {
    return opts.options[0];
  }
  const company = opts.company_name?.trim() ?? '';
  if (company && opts.default !== PlaintiffDefault.BLANK && !opts.hide_company_name) {
    return company;
  }
  return '';
}

/**
 * The plaintiff after the client's options load or change (the firm switching client).
 *
 * A value the user picked or typed (`dirty`) is kept when it is still valid for this client: any
 * text when free text is allowed, an option when restricted. Anything else takes the prefill.
 */
export function resolvePlaintiffValue(
  opts: PlaintiffOptions,
  state: { current: unknown; dirty: boolean; cloneName?: string | null }
): string {
  const current = typeof state.current === 'string' ? state.current.trim() : '';
  if (state.dirty && current) {
    if (!opts.restricted) {
      return current;
    }
    const match = findPlaintiffOption(opts.options, current);
    if (match) {
      return match;
    }
  }
  return plaintiffPrefill(opts, state.cloneName);
}
